import { ItemHeader } from "./item-header";
import { useCradSize } from "@/hooks/use-card-size";
import { ItemCard } from "./item-card";
import { ItemCardSkeletonList } from "./item-card-skeleton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AssetsToolBar } from "@/components/common/assets-tool-bar";
import { assetsMaterialHooks, assetsMaterialItemHooks, assetsTagsHook } from "@/manager";
import { useDownload } from "@/hooks/use-download";
import { TagSection } from "./tags-section";
import { ModalTagMove } from "./modal-tag-move";
import { useCancelSelectOnBlankClick } from "@/hooks/use-cancel-select-on-blank-click";
import { ModalTagChoose } from "./modal-tag-choose";
import { MaterialEmptyImage } from "../assets-material/assets";
import { PlusIcon } from "lucide-react";
import { TextButton } from "../common/text-button";
import { useTranslation } from "react-i18next";
import { MaterialItemData } from "@/services/user-material-get-all-folder-items";
import { useToast } from "@/hooks/use-toast";
import { Upload } from "../common/upload";
import { ALLOW_FILE_TYPES } from "./constant";
import { useParams } from "react-router-dom";
import { MaterialPreviewVideoImageFull } from "../common/material-preview-video-image-full";

// 与 `item-card` 的 `h-[195px]` 一致
const MATERIAL_CARD_HEIGHT = 195;
// 与下方 grid 的 `gap-y-[80px]` 一致，虚拟行高 = 卡片 + 行间间距
const MATERIAL_GRID_ROW_GAP = 80;
const MATERIAL_VIRTUAL_ROW_STRIDE = MATERIAL_CARD_HEIGHT + MATERIAL_GRID_ROW_GAP;

export const AssetsMaterialItem = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const params = useParams();
  // 无限加载逻辑
  const [visibleList, setVisibleList] = useState<MaterialItemData[]>([]);
  const [paddingTop, setPaddingTop] = useState(0);
  const [paddingBottom, setPaddingBottom] = useState(0);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showMoveModal, setShowMoveModal] = useState(false);

  const [showTagChooseModal, setShowTagChooseModal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [activeTagId, setActiveTagId] = useState(0);
  const [previewList, setPreviewList] = useState<{
    id: number,
    url: string,
    videoCoverUrl?: string,
    type: "video" | "image",
    tags?: string[],
    createTime?: number
  }[]>([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  const { downloadFileToZip, download } = useDownload();

  // 记录当前 hover 的预览按钮位置
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);

  // 素材hook
  const { materialItems, loading, currentFolder, fetchMaterialItems } = assetsMaterialItemHooks.useMaterialItemsData();
  const { deleteMaterialItem, loading: deleteLoading } = assetsMaterialItemHooks.useMaterialItemDelete();
  const { uploadMaterialItem } = assetsMaterialItemHooks.useMaterialItemUpload();
  const { renameMaterial } = assetsMaterialHooks.useMaterialFolderRename();
  // 素材标签hook
  const { tags, loading: tagsLoading } = assetsTagsHook.useAssetsTagData();
  const { createTag, loading: createLoading } = assetsTagsHook.useCreateAssetsTag();
  const { deleteTags, loading: tagDeleteLoading } = assetsTagsHook.useDeleteAssetsTags();
  const { renameTag } = assetsTagsHook.useRenameAssetsTag();
  const { batchCopyMove, loading: batchCopyMoveLoading } = assetsTagsHook.useBatchCopyMoveAssetsTags();
  // 素材文件夹hook
  const { createMaterial, loading: createFolderLoading } = assetsMaterialHooks.useMaterialFolderCreate();

  const { ref: containerRef, columns } = useCradSize({
    min: 180,
    max: 250,
    gap: 5,
  });

  // 处理取消选择/退出多选模式
  const handleCancelSelect = () => {
    setSelectedIds([]);
  }

  // 点击空白区域取消选择/退出多选模式
  const cancelSelectOnBlankClick = useCancelSelectOnBlankClick({
    enabled: selectedIds.length > 0,
    // 弹窗打开时阻塞
    blocked: showMoveModal || previewList.length > 0,
    onClick: handleCancelSelect,
  });

  // 处理选中单个文件夹
  const handleSelected = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelectedIds(prev => {
      const nextSet = new Set(prev);
      if (checked) {
        nextSet.add(id);
      } else {
        nextSet.delete(id);
      }
      return Array.from(nextSet);
    });
  }

  // 处理全选/反选所有文件夹
  const handleSelectedAll = (checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        return filteredMaterialItems.map(item => item.id);
      }
      return [];
    });
  }

  // 批量删除
  const handleBatchDelete = async () => {
    handleCancelSelect();
    deleteMaterialItem(selectedIds.map(Number));
  }

  // 单个删除
  const handleSingleDelete = async (id: number) => {
    await deleteMaterialItem([Number(id)]);
  }

  // 处理下载素材压缩包
  const handleDownloadZip = () => {
    const selected = materialItems.filter(item => selectedIds.includes(item.id));

    const list = selected.map(item => ({
      url: item.itemUrl,
      filename: item.itemUrl.split('/').pop() || 'file',
    }));

    downloadFileToZip(list, `${currentFolder?.folderName}.zip`);
    handleCancelSelect();
  }

  // 单个下载
  const handleSingleDownload = (id: number) => {
    const item = materialItems.find(item => item.id === id);
    if (item) {
      download(item.itemUrl, item.itemUrl.split('/').pop() || 'file');
    }
  }

  const handleInvalid = () => {
    toast.default({
      description: t("Please upload JPG, PNG, MP4, MOV files"),
    });
  }

  // 批量移动
  const handleTagMove = async (data: { newFolderId: number; newTagIds: number[]; }[]) => {
    try {
      await batchCopyMove({
        itemIds: selectedIds.map(Number),
        target: data,
        deleteOriginalItem: true,
      });
      handleCancelSelect();
      fetchMaterialItems();
      setPreviewList([]);
      toast.default({
        description: t("assets.material_item.move_modal.move_success"),
      });
    } catch (error) {
      console.error("Failed to move assets material item tags:", error);
      throw error;
    }
  }

  // 批量复制
  const handleTagCopy = async (data: { newFolderId: number; newTagIds: number[]; }[]) => {
    try {
      await batchCopyMove({
        itemIds: selectedIds.map(Number),
        target: data,
      });
      handleCancelSelect();
      if (data.map(item => item.newFolderId).includes(currentFolder?.id)) {
        fetchMaterialItems();
      }
      setPreviewList([]);
      toast.default({
        description: t("assets.material_item.move_modal.copy_success"),
      });
    } catch (error) {
      console.error("Failed to copy assets material item tags:", error);
      throw error;
    }
  }

  // 打开标签选择弹窗
  const handleOpenTagModal = async (files: File[]) => {
    // await uploadMaterialItem(files);
    setFiles(files);
    setShowTagChooseModal(true);
  }

  // 上传
  const handleUpload = async (tagIds: number[]) => {
    await uploadMaterialItem(files, tagIds);
    setShowTagChooseModal(false);
    setFiles([]);
  }

  useEffect(() => {
    setActiveTagId(0);
    handleCancelSelect();
  }, [params.id])

  // 处理文件夹重命名
  const handleFolderRename = (name: string) => {
    if (currentFolder?.id) {
      renameMaterial({ id: currentFolder?.id, name });
    }
  }

  // 删除标签
  const handleDeleteTag = async (ids: number[]) => {
    try {
      await deleteTags(ids);
      setActiveTagId(0);
    } catch (error) {
      console.error("Failed to delete assets material item tags:", error);
      throw error;
    }
  }

  // 处理标签移动+删除标签
  const handleRemoveAndDelete = async (params:
    { tagId: number, newTagIds: number[], moveItemIds: number[] }
  ) => {
    try {
      // 移动
      await batchCopyMove({
        itemIds: params.moveItemIds,
        target: [{
          newFolderId: currentFolder?.id,
          newTagIds: params.newTagIds,
        }],
        deleteOriginalItem: true,
      }, false);
      // 删除
      await deleteTags([params.tagId]);
      // 更新标签列表
      await fetchMaterialItems();
      setActiveTagId(0);
    } catch (error) {
      console.error("Failed to remove and delete assets material item tags:", error);
      throw error;
    }
  }

  // 处理标签点击
  const handleTagClick = (id: number) => {
    setActiveTagId(id);
  }

  // 当前标签下的完整列表（虚拟滚动与空状态都以此为数据源）
  const filteredMaterialItems = useMemo(() => {
    if (activeTagId === 0) {
      return materialItems;
    }
    return materialItems.filter(item => item.tags.some(tag => tag.id === activeTagId));
  }, [activeTagId, materialItems]);

  const updateVisibleList = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const items = filteredMaterialItems;
    const cols = Math.max(1, columns);
    const rowStride = MATERIAL_VIRTUAL_ROW_STRIDE;
    const { scrollTop, clientHeight } = container;

    if (items.length === 0) {
      setVisibleList([]);
      setPaddingTop(0);
      setPaddingBottom(0);
      return;
    }

    const totalRows = Math.ceil(items.length / cols);
    const totalHeight = totalRows * rowStride;

    // 总高度不超出视口时不做虚拟化，避免多余滚动条与 padding 抖动
    if (totalHeight <= clientHeight + 1) {
      setVisibleList(items);
      setPaddingTop(0);
      setPaddingBottom(0);
      return;
    }

    const oversCanRows = 1;
    const startRow = Math.max(0, Math.floor(scrollTop / rowStride) - oversCanRows);
    const endRow = Math.min(
      totalRows - 1,
      Math.floor((scrollTop + clientHeight) / rowStride) + oversCanRows,
    );

    const startIndex = startRow * cols;
    const endIndex = Math.min(items.length, (endRow + 1) * cols);

    setVisibleList(items.slice(startIndex, endIndex));
    setPaddingTop(startRow * rowStride);
    setPaddingBottom((totalRows - endRow - 1) * rowStride);
  }, [filteredMaterialItems, columns]);

  const updateVisibleListRef = useRef(updateVisibleList);
  updateVisibleListRef.current = updateVisibleList;

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = 0;
    }
    updateVisibleListRef.current();
  }, [activeTagId]);

  useEffect(() => {
    updateVisibleListRef.current();
  }, [updateVisibleList]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const run = () => updateVisibleListRef.current();
    container.addEventListener("scroll", run, { passive: true });
    const ro = new ResizeObserver(run);
    ro.observe(container);
    return () => {
      container.removeEventListener("scroll", run);
      ro.disconnect();
    };
  }, []);

  // 预览
  const handlePreview = (item: MaterialItemData, { multiple, id, rect }: { multiple: boolean, id: number, rect: DOMRect }) => {
    if (!multiple) {
      setPreviewList([{
        id: id,
        url: item.itemUrl,
        type: item.itemType as "video" | "image",
        tags: item.tags?.map(tag => tag.tagName),
        videoCoverUrl: item.videoCoverUrl,
        createTime: item.createTime * 1000
      }])
      // 如果有 rect，说明是悬浮预览模式
      if (rect) setHoverRect(rect);
    } else {
      setPreviewList(materialItems.map(item => ({
        id: item.id,
        url: item.itemUrl,
        videoCoverUrl: item.videoCoverUrl,
        type: item.itemType as "video" | "image",
        tags: item.tags?.map(tag => tag.tagName),
        createTime: item.createTime * 1000
      })))
      setCurrentPreviewIndex(materialItems.findIndex(item => item.id === id));
    }
  }

  return (
    <div
      className="flex flex-1 flex-col relative h-full overflow-hidden"
      {...cancelSelectOnBlankClick}
    >
      <div className="flex flex-1 flex-col h-full  overflow-y-auto" ref={containerRef}>
        <ItemHeader
          name={currentFolder?.folderName}
          onUpload={handleOpenTagModal}
        // onRename={handleFolderRename}
        />
        <TagSection
          tags={tags}
          createLoading={createLoading}
          deleteLoading={tagDeleteLoading}
          activeTagId={activeTagId}
          materialItems={materialItems}
          onCreate={createTag}
          onDelete={handleDeleteTag}
          onRemoveAndDelete={handleRemoveAndDelete}
          onRename={renameTag}
          onSelectTag={handleTagClick}
        />
        <div
          className="px-8"
          style={{
            paddingTop: `${paddingTop}px`,
            paddingBottom: `${paddingBottom}px`,
          }}
        >
          <div
            className='grid gap-x-[16px] gap-y-[80px]  '
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {loading && <ItemCardSkeletonList count={12} />}
            {!loading && visibleList.length > 0 && visibleList.map((item) => (
              <ItemCard
                key={item.id}
                id={item.id}
                time={item.createTime * 1000}
                type={item.itemType as "video" | "image"}
                url={item.itemType === 'video' ? item.itemUrl : (item.imageCoverUrl || item.itemUrl)}
                videoCoverUrl={item.videoCoverUrl}
                duration={item.duration}
                deleteLoading={deleteLoading}
                isSelected={selectedIds.includes(item.id)}
                openSelected={selectedIds.length > 0}
                onSelected={handleSelected}
                onDelete={handleSingleDelete}
                onDownload={handleSingleDownload}
                onPreview={({ multiple, id, rect }) => handlePreview(item, { multiple, id: item.id, rect })}
              />
            ))}
          </div>
          {!loading && filteredMaterialItems.length === 0 && (
            <div className="absolute inset-0  flex items-center justify-center">
              <div className="flex flex-col items-center">
                <MaterialEmptyImage className="w-[211px] mb-3" />
                <span className="text-[rgba(102,112,133,1)] text-sm mb-4">
                  {t('assets.material_item.empty_tips')}
                </span>
                <Upload
                  multiple
                  acceptedFileTypes={ALLOW_FILE_TYPES.join(',')}
                  // minFileSize={0}
                  // maxFileSize={MAX_FILE_SIZE}
                  onUpload={handleOpenTagModal}
                  onInvalid={handleInvalid}
                >
                  <TextButton
                    icon={<PlusIcon className="size-5" />}
                    label={t('assets.material_item.upload_file')}
                    className="text-sm font-medium w-[200px]"
                    loading={createLoading}
                    disabled={createLoading}
                  />
                </Upload>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* 感应保护层：仅在悬浮预览且有位置信息时显示 */}
      {previewList.length === 1 && hoverRect && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2000,
            backgroundColor: 'transparent',
            pointerEvents: 'auto'
          }}
          onMouseMove={(e) => {
            const { clientX: x, clientY: y } = e;
            const scope = 10; // 缓冲区
            // 检查坐标是否在矩形范围（外扩 scope 像素）之外
            const isOutside =
              x < hoverRect.left - scope ||
              x > hoverRect.right + scope ||
              y < hoverRect.top - scope ||
              y > hoverRect.bottom + scope;
            if (isOutside) {
              setPreviewList([]);
              setCurrentPreviewIndex(null);
              setHoverRect(null);
            }
          }}
        />
      )}
      {/* 图片查看 */}
      {previewList.length > 0 && (
        <MaterialPreviewVideoImageFull
          previewList={previewList}
          currentIndex={currentPreviewIndex}
          open={previewList.length > 0}
          deleteLoading={deleteLoading}
          onDelete={async (id) => {
            handleSingleDelete(id);
            setPreviewList([]);
          }}
          onMove={async (id) => {
            setSelectedIds([id]);
            setShowMoveModal(true);
          }}
          onOpenChange={(open) => {
            if (!open) {
              setPreviewList([]);
              setCurrentPreviewIndex(null);
            }
          }}
        />
      )}
      {/* 工具栏 */}
      <div
        className={cn(
          "transition-all duration-300 ease-in-out",
          selectedIds.length ? "opacity-100 -translate-y-6" : "opacity-0 translate-y-6 pointer-events-none"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <AssetsToolBar
          showMove
          showDownload
          onClose={handleCancelSelect}
          isSelectedAll={
            filteredMaterialItems.length > 0 &&
            selectedIds.length === filteredMaterialItems.length
          }
          selectedAmount={selectedIds.length}
          onSelectedAll={handleSelectedAll}
          onDelete={handleBatchDelete}
          onDownload={handleDownloadZip}
          onMove={() => setShowMoveModal(true)}
        />
      </div>
      {/* 移动文件夹弹窗 */}
      {showMoveModal && (
        <ModalTagMove
          open={showMoveModal}
          createFolderLoading={createFolderLoading}
          createTagLoading={createLoading}
          saveLoading={batchCopyMoveLoading}
          onOpenChange={setShowMoveModal}
          onCreateFolder={(name) => createMaterial({ name })}
          onCreateTag={(name, folderId) => createTag(name, folderId)}
          onMove={handleTagMove}
          onCopy={handleTagCopy}
        />
      )}
      {/* 标签选择弹窗 */}
      {showTagChooseModal && (
        <ModalTagChoose
          open={showTagChooseModal}
          tags={tags}
          files={files}
          selectedIds={activeTagId !== 0 ? [activeTagId] : []}
          onOpenChange={setShowTagChooseModal}
          onCreate={createTag}
          createLoading={createLoading}
          onConfirm={handleUpload}
          multiple={true}
        />
      )}
    </div>
  )
}
